import React, { createContext, useContext } from 'react';

// Template text markup: [text](https://…) links (optionally [text](url "required")), intake-file:{id} document links,
// **bold** words and lines starting with "- " as bullet lists.
const LINK_SRC = String.raw`\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:|tel:|intake-file:)[^\s)]+)( "required")?\)`;
const TOKEN_RX = new RegExp(`${LINK_SRC}|\\*\\*([^*\\n]+)\\*\\*`, 'g');

export const stripLinks = (s) => String(s ?? '')
  .replace(new RegExp(LINK_SRC, 'g'), '$1').replace(/\*\*([^*\n]+)\*\*/g, '$1');

/** All links in a list of texts: [{href, label, required}] (first occurrence wins, required if marked anywhere). */
export const findLinks = (texts) => {
  const map = new Map();
  texts.forEach((s) => {
    for (const m of String(s ?? '').matchAll(new RegExp(LINK_SRC, 'g'))) {
      const cur = map.get(m[2]) || { href: m[2], label: stripLinks(m[1]), required: false };
      if (m[3]) cur.required = true;
      map.set(m[2], cur);
    }
  });
  return [...map.values()];
};

export const blockTexts = (blocks, lang) => (blocks || []).flatMap((b) => (lang
  ? [(lang === 'pl' && b.label_pl) || b.label, (lang === 'pl' && b.help_pl) || b.help, ...(b.options || [])]
  : [b.label, b.label_pl, b.help, b.help_pl, ...(b.options || [])]));

// fileUrl(fid) -> URL for intake-file links; openFile(fid) for the in-app preview; onOpen(href) records the click.
export const LinkContext = createContext({});

const Anchor = ({ href, label }) => {
  const ctx = useContext(LinkContext);
  const isFile = href.startsWith('intake-file:');
  const fid = isFile ? href.slice('intake-file:'.length) : null;
  const url = isFile ? (ctx.fileUrl ? ctx.fileUrl(fid) : '#') : href;
  const onClick = (e) => {
    e.stopPropagation();
    if (isFile && !ctx.fileUrl && ctx.openFile) { e.preventDefault(); ctx.openFile(fid); }
    ctx.onOpen?.(href);
  };
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" onClick={onClick} onAuxClick={() => ctx.onOpen?.(href)}
      className="text-sky-700 underline underline-offset-2 hover:text-sky-900" data-testid="tpl-text-link" data-href={href}>
      {label}
    </a>
  );
};

const inline = (s, keyBase = '') => {
  const out = [];
  let last = 0;
  for (const m of s.matchAll(TOKEN_RX)) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const k = `${keyBase}${m.index}`;
    if (m[1]) out.push(<Anchor key={k} href={m[2]} label={m[1]} />);
    else out.push(<strong key={k} className="font-semibold">{m[4]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
};

/** Inline rich text (links + bold); with `block`, also line breaks and "- " bullet lists. */
export const LinkText = ({ text, block = false }) => {
  const s = String(text ?? '');
  if (!block) return <>{inline(s)}</>;
  const groups = [];
  s.split('\n').forEach((line) => {
    const li = /^\s*[-•]\s+(.*)$/.exec(line);
    const prev = groups[groups.length - 1];
    if (li) { if (prev?.list) prev.items.push(li[1]); else groups.push({ list: true, items: [li[1]] }); }
    else groups.push({ list: false, line });
  });
  return (
    <>
      {groups.map((g, i) => (g.list
        ? <ul key={i} className="my-1 list-disc space-y-0.5 pl-5">{g.items.map((it, j) => <li key={j}>{inline(it, `${i}-${j}-`)}</li>)}</ul>
        : <span key={i} className="block">{g.line ? inline(g.line, `${i}-`) : '\u00a0'}</span>))}
    </>
  );
};

export default LinkText;
